import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  trigger,
  transition,
  style,
  animate,
  state
} from '@angular/animations';
import { ScrollService } from '../../scroll.service';
import { MESSAGE_DELAY } from 'src/app/shared/constants/global.constants';
import { Subject, take, takeUntil } from 'rxjs';
import { StepAction, Step, StepType } from '../../types/steps.type';
import { StepService } from 'src/app/shared/services/step.service';
import { ChatBotFlowSaService } from '../ui-chat-bot-sa/chat-bot-flow-sa.service';
import { TranslateService } from '@ngx-translate/core';
import { WebShareApiService } from 'src/app/shared/services/web-share-api.service';
import { WordOfMouthChannelsStepData } from '../../types/steps';

const DEFAULT_GOODBYE_REDIRECT_DELAY_SECONDS = 5;

@Component({
  selector: 'user-options',
  templateUrl: './user-options.component.html',
  styleUrls: ['./user-options.component.scss'],
  standalone: false,
  animations: [
    trigger('zoomIn', [
      transition(':enter', [
        style({ transform: 'scale(0.6)', opacity: 0 }), // start state
        animate(
          '{{delay}}ms ease-in',
          style({ transform: 'scale(1)', opacity: 1 })
        ) // end state
      ])
    ]),
    trigger('collapse', [
      state(
        'initial',
        style({
          opacity: 0
        })
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: 1
        })
      ),
      state(
        'closed',
        style({
          height: '0px',
          opacity: 0
        })
      ),
      transition('initial => open', [animate(`0ms ease-in`)]),
      transition('open => closed', [animate(`${MESSAGE_DELAY}ms ease-out`)]),
      transition('closed => open', [animate(`${MESSAGE_DELAY}ms ease-in`)]),
      transition('initial => closed', [animate(`${MESSAGE_DELAY}ms ease-out`)])
    ])
  ]
})
export class UserOptionsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Output() buttonClicked = new EventEmitter<{
    option: StepAction;
    errorMessage?: string;
  }>();
  @Input() testMode?: boolean = false;
  @Input() text: string = '';
  @Input() icon: string = '';
  @Input() step: Step | null = null;
  @Input() currentLang!: string;
  @Input() optionSelected: boolean = false;
  @Input() controlAutoFocusEnabled = false;
  @Input() questionLabelledBy: string | null = null;
  @Input() isLastStep = false;
  // Disables every option except skip (outline) buttons. They stay focusable
  // (`disabledInteractive`) so screen readers can still reach them and hear why
  @Input() primaryOptionsDisabled = false;
  @Output() countdownMessage = new EventEmitter<string>();
  counter: number = DEFAULT_GOODBYE_REDIRECT_DELAY_SECONDS;
  alignCenter: boolean = false;
  isNpsStyle: boolean = false;
  options: StepAction[] = [];
  public animationState = 'initial';
  stepType = StepType;
  isOptionButtonEnabled: boolean = true;
  isHidden: boolean = false;
  isFormal: boolean = false;
  formattedCounterMessage: string = '';
  canUseWebShare: boolean = false;

  constructor(
    private scrollService: ScrollService,
    private cdr: ChangeDetectorRef,
    private stepService: StepService,
    private chatBotFlowSaService: ChatBotFlowSaService,
    private translateService: TranslateService,
    private webShareApiService: WebShareApiService
  ) {}

  ngOnInit(): void {
    this.chatBotFlowSaService.chatFlow$
      .pipe(
        take(1), // Take only the first emission and complete
        takeUntil(this.destroy$)
      )
      .subscribe((flow) => {
        if (flow) {
          this.isFormal = flow.design.is_formal;
        }
      });
    if (this.step?.type === StepType.EMPLOYEE_SELECTION) {
      this.isOptionButtonEnabled = false;
      this.stepService.isUserOptionsButtonDisabled$.subscribe((value) => {
        this.isOptionButtonEnabled = value;
      });
    }
    setTimeout(() => {
      this.scrollService.scrollToBottom();
      this.animationState = 'open';
      this.cdr.detectChanges();
    }, 100);

    if (this.step) {
      this.options = this.step.actions;
      this.filterOnlineReviewPlatformOptions();

      // check IF webShare is supported && enabled
      if (this.step.type === StepType.WORD_OF_MOUTH_CHANNELS) {
        const shareApiOption = this.options.find(
          (option) => option.icon === 'logo-share-api'
        );
        if (shareApiOption) {
          this.canUseWebShare = this.webShareApiService.canUseWebShare();
          // this.canUseWebShare = false;
          if (this.canUseWebShare) {
            this.options = [shareApiOption];
          } else {
            this.options = this.options.filter(
              (option) => option.icon !== 'logo-share-api'
            );
          }
        }
      }
      if (
        this.step.type === StepType.GOODBYE &&
        this.options.length === 1 &&
        typeof this.step.actions[0].action.link === 'object'
      ) {
        this.counter = this.resolveGoodbyeRedirectDelaySeconds();
        // Disable goodbye step redirect if test mode is enabled
        if (!this.testMode) {
          this.startCountdown(
            this.step.actions[0].action.link![this.currentLang]
          );
        }
        return;
      }
      // this triggers next step emit if there is only one option and text is empty -
      // only for the current tail of the conversation, so restoring a batch of past
      // steps in one go (e.g. after a forced reload) doesn't replay old auto-advances
      if (this.options.length === 1 && this.isLastStep) {
        const option = this.options[0];
        if (!option.text[this.currentLang]?.trim()) {
          setTimeout(() => {
            this.onClick(option);
          });
        }
      }

      this.isNpsStyle =
        this.step.type === StepType.RATING_NPS ||
        (this.step.type === StepType.QUESTION &&
          this.options.length === 11 &&
          this.options.every((o) =>
            Object.values(o.text).every((t) => /^\d+$/.test(t.trim()))
          ));

      this.alignCenter =
        this.step.type === StepType.FEEDBACK ||
        this.step.type === StepType.AI_FEEDBACK ||
        this.options.length === 1 ||
        this.isNpsStyle;
    }
  }

  onCollapseDone() {
    if (this.animationState === 'closed') {
      this.isHidden = true;
    }
  }

  get primaryOptionsHintId(): string {
    return `primary-options-hint-${this.step?.step_id}`;
  }

  isPrimaryOptionBlocked(option: StepAction): boolean {
    return (
      this.primaryOptionsDisabled && option.button?.['style'] !== 'outline'
    );
  }

  onClick(option: StepAction) {
    // `disabledInteractive` buttons still emit clicks
    if (this.isPrimaryOptionBlocked(option)) {
      return;
    }
    if (option.icon === 'logo-share-api') {
      const redirectUrl = this.webShareApiService.getChannelUrlFromService(
        option,
        this.currentLang
      );
      if (redirectUrl) {
        this.webShareApiService
          .tryWebShare(redirectUrl)
          .pipe(take(1))
          .subscribe((shareStatus: WordOfMouthChannelsStepData) => {
            if (shareStatus) {
              if (
                this.step?.type === StepType.WORD_OF_MOUTH_CHANNELS &&
                this.step.api?.data
              ) {
                this.step.api.data.share_status = shareStatus.share_status;
                option.text[this.currentLang] = this.translateService.instant(
                  'share_status_' + shareStatus.share_status
                );
              }
              this.executeValidationAndEmit(option);
            }
          });
      }
    } else {
      this.executeValidationAndEmit(option);
    }
  }

  executeValidationAndEmit(option: StepAction) {
    let errorMessage = '';
    if (
      this.step &&
      this.step.type === StepType.FEEDBACK &&
      option.condition_value === 1
    ) {
      const feedbackBlocks = this.step.additional_config?.feedback_blocks || [];
      const feedbackData = this.step.api?.data?.feedbacks || [];

      const requiredFeedbacks = feedbackBlocks.filter((fb) => fb.is_required);
      const requiredCount = requiredFeedbacks.length;

      if (requiredCount > 0) {
        const missingPositions: number[] = [];

        feedbackBlocks.forEach((feedbackBlock, index) => {
          if (feedbackBlock.is_required) {
            const feedbackItem = feedbackData[index];
            let isValid = false;

            if (feedbackBlock.feedback_type === 'checkbox') {
              isValid = !!(
                feedbackItem?.categories && feedbackItem.categories.length > 0
              );
            } else if (
              feedbackBlock.feedback_type === 'textarea' ||
              feedbackBlock.feedback_type === 'textfield'
            ) {
              isValid = !!(
                feedbackItem?.text && feedbackItem.text.trim().length > 0
              );
            }

            if (!isValid) {
              missingPositions.push(index + 1); // 1-based position
            }
          }
        });

        if (missingPositions.length > 0) {
          if (requiredCount === 2 && missingPositions.length === 2) {
            errorMessage = this.processFormOfAddressMessageString(
              'please_provide_answers_to_both_questions'
            );
          } else if (missingPositions.includes(1)) {
            errorMessage = this.processFormOfAddressMessageString(
              'please_answer_the_first_question'
            );
          } else if (missingPositions.includes(2)) {
            errorMessage = this.processFormOfAddressMessageString(
              'please_answer_the_second_question'
            );
          }
        }
      }

      if (requiredCount === 0) {
        const atLeastOneRequired =
          this.step.additional_config?.required_at_least_one_feedback
            .answer_required === true;

        if (atLeastOneRequired) {
          const hasAnyFeedback = feedbackData.some(
            (fd) =>
              (fd.text && fd.text.trim().length > 0) ||
              (fd.categories && fd.categories.length > 0)
          );

          if (!hasAnyFeedback) {
            errorMessage = this.processFormOfAddressMessageString(
              'answer_at_least_one_question'
            );
          }
        }
      }
    }
    // unset Error message if is skip button clicked
    if (
      this.step &&
      this.step.type === StepType.FEEDBACK &&
      option.button &&
      option.button['style'] === 'outline'
    ) {
      errorMessage = '';
    }
    this.buttonClicked.emit({
      option,
      errorMessage: errorMessage || undefined
    });
    if (!errorMessage) {
      this.optionSelected = true;
      this.animationState = 'closed';
    }
  }
  startCountdown(link: string) {
    this.updateCounterMessage();
    const intervalId = setInterval(() => {
      this.counter--;
      this.updateCounterMessage();
      if (this.counter === 0) {
        clearInterval(intervalId);
        window.location.href = link;
      }
    }, 1000);
  }

  private resolveGoodbyeRedirectDelaySeconds(): number {
    const redirectTimer = this.step?.actions?.[0]?.redirect_timer;
    const parsedDelay = Number(redirectTimer);

    if (Number.isFinite(parsedDelay) && parsedDelay >= 1) {
      return Math.floor(parsedDelay);
    }

    return DEFAULT_GOODBYE_REDIRECT_DELAY_SECONDS;
  }

  processFormOfAddressMessageString(message: string): string {
    if (this.currentLang === 'de') {
      const prefix = this.isFormal ? 'formal_' : 'informal_';
      message = prefix + message;
      return message;
    }
    return message;
  }

  private updateCounterMessage(): void {
    const translationKey =
      (this.currentLang === 'de'
        ? this.isFormal
          ? 'formal_'
          : 'informal_'
        : '') + 'redirect_counter_message';

    const formattedCounter = `<span class="counter-fixed-width">${this.counter}</span>`;

    this.formattedCounterMessage = this.translateService.instant(
      translationKey,
      { counter: formattedCounter }
    );
    this.countdownMessage.emit(this.formattedCounterMessage);
  }
  private filterOnlineReviewPlatformOptions(): void {
    // Only run this logic for Online Review steps
    if (this.step?.type !== StepType.ONLINE_REVIEW) {
      return;
    }

    const deviceInfo = this.webShareApiService.getDeviceInfo();

    const isAppleDevice =
      deviceInfo.isIOS ||
      deviceInfo.isIpad ||
      /Macintosh|Mac OS/i.test(navigator.userAgent);

    this.options = this.options.filter((option) => {
      const platform = (option.action as any).platform;

      if (isAppleDevice) {
        return platform !== 'google_play_store';
      } else {
        return platform !== 'apple_app_store';
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
